/**
 * Safe redirect utilities for the /login?redirect=... flow.
 *
 * Security:
 *  - Only allow same-origin redirects (starts with "/" but not "//").
 *  - Reject protocol-relative URLs ("//evil.com/...").
 *  - Reject "javascript:", "data:", etc. URLs.
 *  - Fallback to "/" if the redirect param is missing or unsafe.
 */

const DEFAULT_FALLBACK = '/';

/** True if the string is a safe same-origin path redirect target. */
export function isSafeRedirect(target: string | null | undefined): target is string {
  if (!target) return false;
  if (typeof target !== 'string') return false;
  // Must start with a single "/" and not "//" (protocol-relative)
  if (!target.startsWith('/')) return false;
  if (target.startsWith('//')) return false;
  // Reject encoded backslashes and control chars
  // eslint-disable-next-line no-control-regex -- intentional control-char allow-list for redirect path validation
  if (/[\x00-\x1f]/.test(target)) return false;
  return true;
}

/** Read the `redirect` query param from the current URL, sanitized. */
export function readRedirectParam(fallback: string = DEFAULT_FALLBACK): string {
  if (typeof window === 'undefined') return fallback;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('redirect');
  return isSafeRedirect(raw) ? raw : fallback;
}

/**
 * Build a /login?redirect=... URL pointing at `centralLoginUrl`,
 * preserving the current path+search as the redirect target.
 */
export function buildLoginUrl(centralLoginUrl: string): string {
  if (typeof window === 'undefined') return centralLoginUrl;
  const current = window.location.pathname + window.location.search + window.location.hash;
  const safeRedirect = isSafeRedirect(current) ? current : DEFAULT_FALLBACK;
  const sep = centralLoginUrl.includes('?') ? '&' : '?';
  return `${centralLoginUrl}${sep}redirect=${encodeURIComponent(safeRedirect)}`;
}

/** Hard-navigate to the safe redirect target (or fallback). */
export function navigateToSafeRedirect(fallback: string = DEFAULT_FALLBACK): void {
  if (typeof window === 'undefined') return;
  const target = readRedirectParam(fallback);
  window.location.href = target;
}
